import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, currency, daysUntil, computeMatch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Search, Filter, Star, ArrowRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

export default function Grants() {
  const { user } = useAuth();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [level, setLevel] = useState("all");
  const [state, setState] = useState("all");
  const [eligibility, setEligibility] = useState(user?.org_type || "all");
  const [cats, setCats] = useState({ categories: [], states: [] });

  useEffect(() => {
    api.get("/api/grants/categories").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    if (level !== "all") params.level = level;
    if (state !== "all") params.state = state;
    if (eligibility !== "all") params.eligibility = eligibility;
    api.get("/api/grants/", { params }).then((r) => setGrants(r.data)).finally(() => setLoading(false));
  }, [q, category, level, state, eligibility]);

  const sorted = useMemo(() => {
    return [...grants].map((g) => ({ ...g, match: computeMatch(g, user) })).sort((a, b) => b.match - a.match);
  }, [grants, user]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <div className="overline mb-2">DISCOVER</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="grants-title">Grants across every US state.</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">Federal, state, and foundation opportunities — matched to your organization profile and ranked by fit.</p>
        </div>

        {/* Search + filters */}
        <div className="gf-card p-4 lg:p-5 flex flex-col lg:flex-row gap-3 lg:items-center" data-testid="grants-filters">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, agency, keyword..."
              className="w-full h-11 pl-9 pr-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
              data-testid="grants-search" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-11 lg:w-40" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>
              {cats.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="h-11 lg:w-36" data-testid="filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="federal">Federal</SelectItem>
              <SelectItem value="state">State</SelectItem>
              <SelectItem value="foundation">Foundation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="h-11 lg:w-32" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All states</SelectItem>
              {cats.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={eligibility} onValueChange={setEligibility}>
            <SelectTrigger className="h-11 lg:w-40" data-testid="filter-eligibility"><SelectValue placeholder="Eligibility" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any org type</SelectItem>
              <SelectItem value="llc">LLC</SelectItem>
              <SelectItem value="nonprofit">Nonprofit</SelectItem>
              <SelectItem value="sole_prop">Sole Prop</SelectItem>
              <SelectItem value="corp">Corporation</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* results */}
        <div className="mt-8">
          <div className="text-sm text-neutral-500 mb-4" data-testid="results-count">
            {loading ? "Loading grants…" : `${sorted.length} grants match your filters`}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sorted.map((g) => {
              const d = daysUntil(g.deadline);
              const urgent = d < 14;
              return (
                <Link to={`/grants/${g.id}`} key={g.id} className="gf-card p-6 block group" data-testid={`grant-card-${g.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <Badge variant="outline" className="font-medium">{g.category}</Badge>
                    <div className="text-right">
                      <div className="font-display font-bold text-lg leading-none">{g.match}%</div>
                      <div className="text-[10px] text-neutral-500 uppercase tracking-widest">match</div>
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-semibold leading-snug mb-2 group-hover:text-[color:var(--accent)] transition-colors">{g.title}</h3>
                  <div className="text-xs text-neutral-600 mb-4">{g.agency}</div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Amount</div>
                      <div className="font-medium">{currency(g.amount_min)} – {currency(g.amount_max)}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 uppercase tracking-widest text-[10px]">Deadline</div>
                      <div className={`font-medium ${urgent ? "text-red-600" : ""}`}>{d}d — {new Date(g.deadline).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="flex gap-1 flex-wrap">
                      {(g.eligibility || []).slice(0, 3).map((e) => (
                        <Badge key={e} variant="secondary" className="text-[10px]">{e}</Badge>
                      ))}
                    </div>
                    <ArrowRight size={16} className="text-neutral-400 group-hover:text-[color:var(--accent)] group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
